import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User, AuthChangeEvent } from '@supabase/supabase-js';

// Helper functions to access user metadata
export const getUserFirstName = (user: User | null): string => {
  return user?.user_metadata?.first_name || '';
};

export const getUserLastName = (user: User | null): string => {
  return user?.user_metadata?.last_name || '';
};

export const getUserFullName = (user: User | null): string => {
  const firstName = getUserFirstName(user);
  const lastName = getUserLastName(user);
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  } else {
    return user?.email?.split('@')[0] || 'User';
  }
};

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const userRef = useRef<User | null>(null);

  console.log('👤 useUser: Hook called, current state:', {
    hasUser: !!user,
    userId: user?.id,
    loading,
    error,
    hasImpersonatedUser: !!impersonatedUser,
    currentPath: window.location.pathname
  });

  // Function to check and set impersonation
  const checkImpersonation = (currentUser: User | null) => {
    console.log('🎭 useUser: Checking impersonation for user:', currentUser?.id);
    
    if (!currentUser) {
      console.log('🎭 useUser: No user, clearing impersonation');
      setImpersonatedUser(null);
      return;
    }
    
    const impersonatedUserId = localStorage.getItem("impersonatedUserId");
    const originalUserId = localStorage.getItem("originalUserId");
    
    console.log('🎭 useUser: Impersonation data from localStorage:', {
      impersonatedUserId,
      originalUserId
    });
    
    if (impersonatedUserId && originalUserId) {
      const impersonated = {
        ...currentUser,
        id: impersonatedUserId,
        user_metadata: {
          ...currentUser.user_metadata,
          isImpersonated: true,
          originalUserId: currentUser.id
        }
      };
      console.log('🎭 useUser: Setting impersonated user:', impersonatedUserId);
      setImpersonatedUser(impersonated);
    } else {
      console.log('🎭 useUser: No impersonation data, clearing impersonated user');
      setImpersonatedUser(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    console.log('🔄 useUser: useEffect mounted, starting auth setup');

    // Get initial user
    const getInitialUser = async () => {
      try {
        console.log('🔍 useUser: Fetching initial user...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        console.log('🔍 useUser: Initial session check result:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          error: sessionError ? sessionError.message : null
        });
        
        if (sessionError) {
          console.error('❌ useUser: Session error:', sessionError);
          if (mounted) {
            setError(sessionError.message);
            setLoading(false);
          }
          return;
        }

        if (!session) {
          console.log('ℹ️ useUser: No active session found');
          if (mounted) {
            setUser(null);
            setError(null);
            setLoading(false);
          }
          return;
        }

        const { data: { user: initialUser }, error: userError } = await supabase.auth.getUser();
        
        console.log('🔍 useUser: getUser result:', {
          hasUser: !!initialUser,
          userId: initialUser?.id,
          error: userError ? userError.message : null
        });
        
        if (userError) {
          console.error('❌ useUser: Error fetching user:', userError);
          if (mounted) {
            setError(userError.message);
            setLoading(false);
          }
          return;
        }

        console.log('✅ useUser: Initial user fetched successfully:', initialUser?.id);
        if (mounted) {
          setUser(initialUser);
          userRef.current = initialUser;
          checkImpersonation(initialUser);
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ useUser: Unexpected error in getInitialUser:', error);
        if (mounted) {
          setError(error instanceof Error ? error.message : 'An unexpected error occurred');
          setLoading(false);
        }
      }
    };

    getInitialUser();

    // Listen for auth changes
    console.log('👂 useUser: Setting up auth state change listener');
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      console.log('🔔 useUser: Auth state changed:', {
        event,
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id,
        currentPath: window.location.pathname
      });
      
      const currentUser = session?.user ?? null;
      
      // Handle different auth events
      switch (event) {
        case 'SIGNED_OUT':
          console.log('🚪 useUser: Handling SIGNED_OUT event');
          setUser(null);
          setError(null);
          setLoading(false);
          break;
        case 'SIGNED_IN':
          console.log('🔑 useUser: Handling SIGNED_IN event');
          setUser(currentUser);
          setError(null);
          setLoading(false);
          break;
        case 'PASSWORD_RECOVERY':
          console.log('🔐 useUser: Handling PASSWORD_RECOVERY event');
          setUser(currentUser);
          setError(null);
          setLoading(false);
          break;
        case 'USER_UPDATED':
          console.log('👤 useUser: Handling USER_UPDATED event');
          setUser(currentUser);
          setError(null);
          break;
        case 'INITIAL_SESSION':
          console.log('🏁 useUser: Handling INITIAL_SESSION event');
          setUser(currentUser);
          setLoading(false);
          break;
        default:
          console.log('❓ useUser: Handling unknown event:', event);
          setUser(currentUser);
      }
      
      userRef.current = currentUser;
      checkImpersonation(currentUser);
    });

    // Also listen for storage changes (in case impersonation is toggled in another tab)
    const handleStorageChange = (event: StorageEvent) => {
      console.log('💾 useUser: Storage change detected:', {
        key: event.key,
        newValue: event.newValue,
        oldValue: event.oldValue
      });
      
      if (event.key === "impersonatedUserId" || event.key === "originalUserId") {
        console.log('🎭 useUser: Impersonation storage changed, rechecking');
        checkImpersonation(userRef.current);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      console.log('🧹 useUser: Cleaning up useEffect');
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Return the impersonated user if available, otherwise the actual user
  const currentUser = impersonatedUser || user;
  
  console.log('📤 useUser: Returning user data:', {
    hasCurrentUser: !!currentUser,
    currentUserId: currentUser?.id,
    hasActualUser: !!user,
    actualUserId: user?.id,
    isImpersonating: !!impersonatedUser,
    loading,
    error
  });
  
  return { 
    user: currentUser, 
    actualUser: user,
    isImpersonating: !!impersonatedUser,
    loading,
    error,
    firstName: getUserFirstName(currentUser),
    lastName: getUserLastName(currentUser),
    fullName: getUserFullName(currentUser)
  };
}; 